import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyRadioChange as MatRadioChange } from '@angular/material/legacy-radio';
import {
  NgxScannerQrcodeComponent,
  ScannerQRCodeConfig,
  ScannerQRCodeDevice,
} from 'ngx-scanner-qrcode';
import { Subject } from 'rxjs';
import { delay, takeUntil } from 'rxjs/operators';

// ANIMATIONS
import { LocalStorageKeys } from '../../../shared';
import { slideInAnimation } from '../../../shared/animations/animation';
import { DialogConfig } from '../../../shared/configs/dialog.config';
import { LocationWarehouseAppScannerPopupBarcodeComponent } from '../location-warehouse-app-scanner-popup-barcode/location-warehouse-app-scanner-popup-barcode.component';

// LIBS

@Component({
  selector: 'lum-location-warehouse-app-scanner-devices',
  templateUrl: './location-warehouse-app-scanner-devices.component.html',
  styleUrls: ['./location-warehouse-app-scanner-devices.component.scss'],
  animations: [slideInAnimation],
})
export class LocationWarehouseAppScannerDevicesComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild(NgxScannerQrcodeComponent, { static: false })
  scanner :NgxScannerQrcodeComponent;

  loading :boolean = false;
  devices :ScannerQRCodeDevice[] = [];

  scannerConfig :ScannerQRCodeConfig = {
    fps: 10,
    vibrate: 300,
    isBeep: false,
    constraints: { audio: false, video: true },
  };

  currentDeviceId :string;
  selectedDevice :ScannerQRCodeDevice | null = null;
  showDevicesList :boolean = true;

  private destroy$ = new Subject<void>();
  constructor(
    private cd :ChangeDetectorRef,
    private dialog :MatDialog,
  ) {}

  ngOnInit() :void {
    this.loading = true;
  }

  ngOnDestroy() :void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit() {
    this.scanner.isReady.pipe(delay(1000), takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.handle(this.scanner, 'start');
        this.loadDevices();
      },
      error: (err) => {
        this.loading = false;
        this.cd.detectChanges();

        return err;
      },
    });
  }

  handle(scanner :any, fn :string) :void {
    const playDeviceFacingBack = (devices :ScannerQRCodeDevice[]) => {
      // front camera or back camera check here!
      const device = devices.find((f) => /back|rear|environment/gi.test(f.label)); // Default Back Facing Camera
      const activeDevice = this.currentDeviceId
        ? this.currentDeviceId
        : device
          ? device.deviceId
          : devices[0].deviceId;
      scanner.playDevice(activeDevice);
    };
    if (fn === 'start') {
      scanner[fn](playDeviceFacingBack);
    } else {
      scanner[fn]();
    }
  }

  onOpenBarcodeDialog() :void {
    this.dialog.open(LocationWarehouseAppScannerPopupBarcodeComponent, {
      width: DialogConfig.sizes.medium,
    });
  }

  onSelectedDeviceIdChange(deviceId :string) :void {
    const device :ScannerQRCodeDevice | undefined = this.devices.find(
      (device :ScannerQRCodeDevice) => device.deviceId === deviceId,
    );
    if (device) {
      localStorage.setItem(
        LocalStorageKeys.WAREHOUSE_APP_ACTIVE_DEVICE_ID_KEY,
        deviceId,
      );
      this.selectedDevice = device;
    } else {
      this.selectedDevice = null;
    }
  }

  private loadDevices() :void {
    this.scanner.devices
      .pipe(takeUntil(this.destroy$))
      .subscribe((devices :ScannerQRCodeDevice[]) => {
        this.devices = devices;
        this.loading = false;
        this.autoSelectDevice();
        this.cd.detectChanges();
      });
  }

  private autoSelectDevice() :void {
    const storedDeviceId :string | null = localStorage.getItem(
      LocalStorageKeys.WAREHOUSE_APP_ACTIVE_DEVICE_ID_KEY,
    );
    if (!this.devices.length || !storedDeviceId) {
      return;
    }
    const device :ScannerQRCodeDevice | undefined = this.devices.find(
      (device :ScannerQRCodeDevice) => device.deviceId === storedDeviceId,
    );
    if (device) {
      this.currentDeviceId = device.deviceId;
      this.selectedDevice = device;
      this.showDevicesList = false;
    } else {
      localStorage.removeItem(LocalStorageKeys.WAREHOUSE_APP_ACTIVE_DEVICE_ID_KEY);
      this.selectedDevice = null;
    }
  }

  get title() :string {
    if (this.loading) {
      this.loading ? 'Loading...' : 'Please Select a Device';
    } else if (!this.loading && this.devices?.length === 0) {
      return 'No Devices Found';
    }

    return 'Please Select a Device';
  }
}
